/**
 * Search-as-you-type with a suggestion dropdown.
 *
 * Implements the combobox keyboard contract: Down/Up move through results,
 * Enter opens the highlighted one, Escape dismisses. Without that, a dropdown
 * is mouse-only and unusable from the keyboard.
 */

import { $, $$, debounce, money, on } from '@/lib/dom'
import { api, withBase, type SuggestionDTO } from '@/lib/api'

export function initSearch(): void {
  const forms = $$('[data-search]')
  if (forms.length === 0) return

  forms.forEach((form) => {
    const input = $<HTMLInputElement>('[data-search-input]', form)
    const results = $<HTMLElement>('[data-search-results]', form)
    if (!input || !results) return

    let items: SuggestionDTO[] = []
    let active = -1

    const close = (): void => {
      results.hidden = true
      input.setAttribute('aria-expanded', 'false')
      active = -1
    }

    const paintActive = (): void => {
      $$('.search__result', results).forEach((node, i) => {
        node.classList.toggle('is-active', i === active)
        if (i === active) node.scrollIntoView({ block: 'nearest' })
      })
    }

    const render = (): void => {
      if (items.length === 0) {
        results.innerHTML = '<p class="search__empty">No products match that search.</p>'
      } else {
        results.innerHTML = items
          .map((item, i) => {
            const art = item.gradient.replace(/["'<>]/g, '')

            return `
              <a class="search__result" href="${withBase('/product.php')}?slug=${encodeURIComponent(item.slug)}"
                 role="option" id="search-opt-${i}" aria-selected="false">
                <span class="search__result-art" style="background:linear-gradient(135deg, ${art})"></span>
                <span>
                  <span class="search__result-brand">${escapeHtml(item.brand)}</span>
                  <span class="search__result-name">${escapeHtml(item.name)}</span>
                </span>
                <span class="price" style="margin-left:auto">${money(item.price)}</span>
              </a>`
          })
          .join('')
      }

      results.hidden = false
      input.setAttribute('aria-expanded', 'true')
      paintActive()
    }

    const lookup = debounce((term: string) => {
      void api
        .suggest(term)
        .then((response) => {
          items = response.results
          active = -1
          render()
        })
        .catch(() => close())
    }, 220)

    on(input, 'input', () => {
      const term = input.value.trim()
      // Two characters is the point where results stop being noise.
      if (term.length < 2) {
        close()

        return
      }
      lookup(term)
    })

    on(input, 'keydown', (event) => {
      const e = event

      if (e.key === 'Escape') {
        close()

        return
      }

      if (results.hidden || items.length === 0) return

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        active =
          e.key === 'ArrowDown'
            ? (active + 1) % items.length
            : (active - 1 + items.length) % items.length
        paintActive()
      }

      if (e.key === 'Enter' && active >= 0) {
        e.preventDefault()
        $$('.search__result', results)[active]?.click()
      }
    })

    // Close when focus or the pointer leaves the whole combobox.
    on(document, 'click', (event) => {
      if (!form.contains(event.target as Node)) close()
    })
    on(input, 'focus', () => {
      if (items.length > 0 && input.value.trim().length >= 2) render()
    })
  })
}

function escapeHtml(value: string): string {
  const node = document.createElement('span')
  node.textContent = value

  return node.innerHTML
}
